import React, { useEffect, useMemo, useState } from 'react';
import { Button, Card, CardFooter, Col, Row } from 'reactstrap';
import { endOfDay, startOfDay } from 'date-fns';
import { useDispatch } from 'react-redux';
import { useFormik } from 'formik';
import * as Yup from 'yup';

import FormDateInput from '../../components/form/FormDateInput';
import AdminHeader from '../../components/header/AdminHeader';
import CallexLoader from '../../components/CallexLoader';
import FormSelect from '../../components/form/FormSelect';
import Pagination from '../../components/form/Pagination';
import { formatArchiveOptions, formatPayloadDate, getDateRangeFromLabel } from '../../utils/common';

import {
  archiveOptionsGet,
  missedCallReportExport
} from '../../store/slices/tenantSlices/dailyReportSlice';
import { useUserDetails } from '../../store/slices/authSlice';
import { didOptionGet } from '../../store/slices/tenantSlices/incomingSlice';
import { useTimezone } from '../../store/slices/commonSlice';
import { toZonedTime } from 'date-fns-tz';
import { agentMissedCallReportGet } from '../../store/slices/callHistorySlice';

const MyMissedCallReport = () => {
  const dispatch = useDispatch();
  const timezone = useTimezone();
  const user = useUserDetails();
  const today = toZonedTime(new Date(), timezone);

  const [isFilterOpen, setIsFilterOpen] = useState(true);
  const [didOptions, setDidOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [isExporting, setIsExporting] = useState(false);
  const [archiveOptions, setArchiveOptions] = useState([]);

  const initialValues = {
    page: 1,
    limit: 10,
    start_date: startOfDay(today),
    end_date: endOfDay(today),
    did_number: 'all',
    archiveId: 0
  };

  const validationSchema = Yup.object({
    start_date: Yup.date().required('Start date is required'),
    end_date: Yup.date().required('End date is required')
  });

  const getDidOption = async () => {
    try {
      const response = await dispatch(didOptionGet()).unwrap();
      if (response && response.data) {
        const options = response.data.map((val) => ({
          label: val.number || '-',
          value: val.number
        }));
        setDidOptions([{ label: 'All', value: 'all' }, ...options]);
      } else {
        setDidOptions([{ label: 'All', value: 'all' }]);
      }
    } catch (error) {
      setDidOptions([]);
      console.log('error', error);
    }
  };
  const getArchiveOptions = async () => {
    try {
      const response = await dispatch(archiveOptionsGet()).unwrap();
      if (response && response.data) {
        const options = formatArchiveOptions(response.data, timezone);
        setArchiveOptions(options);
      } else {
        setArchiveOptions([]);
      }
    } catch (error) {
      setArchiveOptions([]);
      console.log('error', error);
    }
  };

  useEffect(() => {
    getArchiveOptions();
    getDidOption();
  }, []);

  const fetchReportData = async (overrides = {}) => {
    setIsLoading(true);
    setIsFilterOpen(false);
    try {
      const currentValues = { ...values, ...overrides };
      const formattedStartDate = formatPayloadDate(currentValues.start_date);
      const formattedEndDate = formatPayloadDate(currentValues.end_date);
      const payload = {
        ...currentValues,
        start_date: formattedStartDate,
        end_date: formattedEndDate
      };

      const response = await dispatch(agentMissedCallReportGet(payload)).unwrap();
      if (response) {
        setReportData(response || []);
      }
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      console.log('error', error);
    }
  };

  const onSubmit = (formValues, { setFieldValue }) => {
    setFieldValue('page', 1);
    fetchReportData({ ...formValues, page: 1 });
  };

  const { values, handleBlur, setFieldValue, handleSubmit, handleChange, touched, errors } =
    useFormik({
      initialValues,
      validationSchema,
      onSubmit
    });



  const handleExportReport = async () => {
    setIsExporting(true);
    try {
      const formattedStartDate = formatPayloadDate(values.start_date);
      const formattedEndDate = formatPayloadDate(values.end_date);
      const payload = {};
      payload['start_date'] = formattedStartDate;
      payload['end_date'] = formattedEndDate;
      payload['did_number'] = values?.did_number;
      payload['processId'] = user?.process_details?.process_id || '';
      payload['archiveId'] = values.archiveId;
      const response = await dispatch(missedCallReportExport(payload)).unwrap();
      const url = window.URL.createObjectURL(response);
      const link = document.createElement('a');
      link.href = url;
      link.download;
      link.click();
      window.URL.revokeObjectURL(url);
      setIsExporting(false);
    } catch (error) {
      setIsExporting(false);
      console.log('Error exporting CSV:', error);
    }
  };

  const selectedOption = useMemo(
    () => archiveOptions.find((opt) => opt.value === values.archiveId),
    [values.archiveId, archiveOptions]
  );

  const selectedLabel = selectedOption?.label || null;

  const { minDate, maxDate } = useMemo(() => {
    return getDateRangeFromLabel(selectedLabel, timezone);
  }, [selectedLabel, timezone]);

  useEffect(() => {
    if (minDate && maxDate) {
      setFieldValue('start_date', minDate);
      setFieldValue('end_date', maxDate);
    }
  }, [minDate?.getTime(), maxDate?.getTime()]);

  return (
    <div>
      <AdminHeader
        title="Missed Call Report"
        isSearch={true}
        onSearchClick={() => setIsFilterOpen(!isFilterOpen)}
        isManualExport
        isExporting={isExporting}
        isExport={reportData?.data?.length > 0}
        onExportClick={() => handleExportReport()}
      />
      {isFilterOpen && (
        <Card className="mb-3 p-3">
          <form onSubmit={handleSubmit}>
            <Row>
              <Col lg="2" md="2" className="flex-row">
                <FormSelect
                  label="Month"
                  placeholder="Select month"
                  value={values.archiveId}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  name="archiveId"
                  options={archiveOptions}
                  touched={touched}
                  errors={errors}
                />
              </Col>
              <Col lg="3" md="3" className="flex-row">
                <FormDateInput
                  name="start_date"
                  dateFormat="yyyy-MM-dd HH:mm:ss"
                  label="From Date"
                  showTimeSelect={true}
                  minDate={minDate}
                  maxDate={values.end_date || maxDate}
                  value={values.start_date}
                  onChange={(e) => setFieldValue('start_date', e.target.value)}
                  onBlur={handleBlur}
                  touched={touched}
                  errors={errors}
                />
              </Col>
              <Col lg="3" md="3" className="flex-row">
                <FormDateInput
                  name="end_date"
                  label="To Date"
                  dateFormat="yyyy-MM-dd HH:mm:ss"
                  showTimeSelect={true}
                  minDate={values.start_date || minDate}
                  maxDate={maxDate}
                  value={values.end_date}
                  onChange={(e) => setFieldValue('end_date', e.target.value)}
                  onBlur={handleBlur}
                  touched={touched}
                  errors={errors}
                />
              </Col>
              <Col lg="3" md="3" className="flex-row">
                <FormSelect
                  name="did_number"
                  label="DID Number"
                  placeholder="Select DID Number"
                  value={values.did_number}
                  options={didOptions}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  touched={touched}
                  errors={errors}
                />
              </Col>
            </Row>
            <CardFooter className="p-0 bg-white text-end">
              <Button color="primary" className="px-4" type="submit">
                Search
              </Button>
            </CardFooter>
          </form>
        </Card>
      )}
      {isLoading ? (
        <Card
          className="p-0 d-flex justify-content-center align-items-center m-0"
          style={{ height: isFilterOpen ? 'calc(100vh - 328px)' : 'calc(100vh - 165px)' }}>
          <CallexLoader />
        </Card>
      ) : reportData?.data?.length > 0 ? (
        <Card className="p-0 position-relative">
          <div
            className="daily-report-table"
            style={{ height: isFilterOpen ? 'calc(100vh - 369px)' : 'calc(100vh - 208px)' }}>
            <table className="report-list-table">
              <thead>
                <tr>
                  {reportData?.header?.map((val, index) => {
                    return <th key={index}>{val.label}</th>;
                  })}
                </tr>
              </thead>
              <tbody>
                {reportData?.data?.map((val, index) => {
                  return (
                    <tr key={index}>
                      {reportData?.header?.map((value, idx) => {
                        return <td key={idx}>{val[value?.name] || '-'}</td>;
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="position-absolute w-100" style={{ bottom: '-56px' }}>
            <Pagination
              totalCount={reportData?.total}
              limit={reportData?.limit}
              page={reportData?.page}
              onLimitChange={(val) => {
                setFieldValue('limit', val);
                setFieldValue('page', 1);
                fetchReportData({ limit: val, page: 1 });
              }}
              onPageClick={(val) => {
                setFieldValue('page', val);
                fetchReportData({ page: val });
              }}
              lastPageClick={(val) => {
                setFieldValue('page', val);
                fetchReportData({ page: val });
              }}
              firstPageClick={(val) => {
                setFieldValue('page', val);
                fetchReportData({ page: val });
              }}
              onRefreshClick={() => fetchReportData()}
            />
          </div>
        </Card>
      ) : (
        <Card
          className="p-0 d-flex justify-content-center align-items-center no-data m-0"
          style={{ height: isFilterOpen ? 'calc(100vh - 330px)' : 'calc(100vh - 170px)' }}>
          No data available
        </Card>
      )}
    </div>
  );
};

export default MyMissedCallReport;
